import { API_BASE_URL } from 'config/api'

export interface ProductImage {
  image: string
  thumbnail: string | null
  sizeKB: number | null
  width: number | null
  height: number | null
  alt?: string
  title?: string
  isPrimary?: boolean
}

/**
 * Returns a meaningful alt string for a product image.
 * Priority: image-specific alt → product title (with optional index) → generic fallback.
 */
export function getImageAlt(
  imageAlt?: string,
  productTitle?: string,
  index?: number,
): string {
  if (imageAlt && imageAlt.trim()) return imageAlt.trim()
  if (productTitle && productTitle.trim()) {
    return typeof index === 'number'
      ? `${productTitle.trim()} - تصویر ${index + 1}`
      : productTitle.trim()
  }
  return 'تصویر محصول'
}

/**
 * Get full image URL
 */
export function getImageUrl(filename: string): string {
  if (!filename) return ''
  return `${API_BASE_URL}/uploads/${filename}`
}

/**
 * Get thumbnail URL
 */
export function getThumbnailUrl(filename: string | null): string {
  if (!filename) return ''
  return `${API_BASE_URL}/uploads/thumbs/${filename}`
}

/**
 * Get the best image URL (thumbnail if available, otherwise full image)
 */
export function getBestImageUrl(
  img: ProductImage,
  useThumbnail: boolean = true,
): string {
  if (useThumbnail && img.thumbnail) {
    return getThumbnailUrl(img.thumbnail)
  }
  return getImageUrl(img.image)
}

/**
 * Preload a single image
 */
export function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = url
  })
}

/**
 * Preload product images (first thumbnail and full image)
 */
export function preloadProductImages(images: ProductImage[]): void {
  // Preload first image thumbnail (for list views)
  if (images[0]?.thumbnail) {
    preloadImage(getThumbnailUrl(images[0].thumbnail))
  }

  // Preload first full image (for detail views)
  if (images[0]?.image) {
    preloadImage(getImageUrl(images[0].image))
  }
}

/**
 * Get product image URL with backward compatibility
 * Handles multiple formats:
 * - New format: images array (ProductImage[])
 * - Cart/Order format: image as ProductImage object
 * - Old format: image as string
 * @param product - Product object with various image formats
 * @param useThumbnail - Whether to use thumbnail (default: true)
 * @returns Image URL or empty string if no image available
 */
export function getProductImageUrl(
  product: { image?: string | ProductImage; images?: ProductImage[] },
  useThumbnail: boolean = true,
): string {
  // New format: images array (ProductImage[])
  if (product.images && product.images.length > 0) {
    return getBestImageUrl(product.images[0], useThumbnail)
  }

  // Cart/Order format: image as ProductImage object
  if (
    product.image &&
    typeof product.image === 'object' &&
    'image' in product.image
  ) {
    return getBestImageUrl(product.image as ProductImage, useThumbnail)
  }

  // Old format: single image string
  if (product.image && typeof product.image === 'string') {
    // For old format, we'll use the new endpoint structure
    // If thumbnail is requested but not available, fall back to full image
    return getImageUrl(product.image)
  }

  // No image available
  return ''
}
